import { useEffect, useState } from "react";
import { languageFlag, languageName } from "./LanguageFlagSelector";

type ThemeMode = "light" | "dark";
type HelpSection = { icon: string; title: string; description: string; steps: string[] };
type HelpContent = {
  toolbar: string;
  light: string;
  dark: string;
  help: string;
  close: string;
  kicker: string;
  title: string;
  intro: string;
  sections: HelpSection[];
};

const helpCopy: Record<"es" | "en" | "pt", HelpContent> = {
  es: {
    toolbar: "Idioma, apariencia y ayuda",
    light: "Usar modo claro",
    dark: "Usar modo oscuro",
    help: "Abrir ayuda",
    close: "Cerrar ayuda",
    kicker: "ORGANIZE · GUÍA DEL SITIO",
    title: "Cómo organizar un evento",
    intro: "Abrí cada opción para ver el recorrido recomendado y saber dónde realizar cada tarea.",
    sections: [
      { icon: "01", title: "Crear y configurar el evento", description: "Datos generales, identidad visual, idiomas y permisos.", steps: ["Entrá en Eventos y creá o seleccioná el evento.", "Completá fechas, sedes, idiomas, colores, banners y patrocinadores.", "Revisá en Configuración los permisos y servicios disponibles."] },
      { icon: "02", title: "Armar la agenda", description: "Días, salas, sesiones y participaciones en orden.", steps: ["Creá los días, salones y categorías necesarias.", "En Agenda agregá cada sesión con su horario y duración.", "Dentro de la sesión asigná faculty, roles, títulos y minutos de intervención."] },
      { icon: "03", title: "Gestionar personas", description: "Faculty, organizadores, usuarios y expositores.", steps: ["Cargá o importá el faculty y verificá sus datos de contacto.", "Asigná roles y permisos solamente para el evento activo.", "Usá Usuarios y Organigrama para controlar responsabilidades y accesos."] },
      { icon: "04", title: "Comunicar y coordinar", description: "WhatsApp, correo, convocatorias y seguimiento.", steps: ["Vinculá WhatsApp y revisá el estado de conexión.", "Prepará mensajes o correos segmentados por sesión y participante.", "Controlá entregas, respuestas y recordatorios desde Comunicaciones."] },
      { icon: "05", title: "Abstracts y resultados", description: "Recepción científica, selección, reportes y métricas.", steps: ["Configurá Call for Science, modalidades, áreas y fechas de recepción.", "Revisá trabajos, asignaciones y decisiones sin mezclar eventos.", "Consultá Reportes para verificar agenda, participantes y actividad."] },
    ],
  },
  en: {
    toolbar: "Language, appearance and help",
    light: "Use light mode",
    dark: "Use dark mode",
    help: "Open help",
    close: "Close help",
    kicker: "ORGANIZE · SITE GUIDE",
    title: "How to organize an event",
    intro: "Open each option to see the recommended workflow and where to complete every task.",
    sections: [
      { icon: "01", title: "Create and configure the event", description: "General information, visual identity, languages and permissions.", steps: ["Open Events and create or select the event.", "Complete dates, venues, languages, colors, banners and sponsors.", "Review permissions and available services in Settings."] },
      { icon: "02", title: "Build the schedule", description: "Days, rooms, sessions and ordered participations.", steps: ["Create the required days, rooms and categories.", "Add every session with its time and duration under Schedule.", "Assign faculty, roles, titles and speaking time inside each session."] },
      { icon: "03", title: "Manage people", description: "Faculty, organizers, users and exhibitors.", steps: ["Add or import faculty and verify their contact details.", "Assign roles and permissions only for the active event.", "Use Users and Organization Chart to control responsibilities and access."] },
      { icon: "04", title: "Communicate and coordinate", description: "WhatsApp, email, invitations and follow-up.", steps: ["Connect WhatsApp and check its connection status.", "Prepare messages or emails by session and participant.", "Track delivery, replies and reminders under Communications."] },
      { icon: "05", title: "Abstracts and results", description: "Scientific submissions, selection, reports and metrics.", steps: ["Configure Call for Science, modalities, topics and submission dates.", "Review works, assignments and decisions without mixing events.", "Open Reports to verify the schedule, participants and activity."] },
    ],
  },
  pt: {
    toolbar: "Idioma, aparência e ajuda",
    light: "Usar modo claro",
    dark: "Usar modo escuro",
    help: "Abrir ajuda",
    close: "Fechar ajuda",
    kicker: "ORGANIZE · GUIA DO SITE",
    title: "Como organizar um evento",
    intro: "Abra cada opção para ver o fluxo recomendado e onde realizar cada tarefa.",
    sections: [
      { icon: "01", title: "Criar e configurar o evento", description: "Dados gerais, identidade visual, idiomas e permissões.", steps: ["Entre em Eventos e crie ou selecione o evento.", "Complete datas, locais, idiomas, cores, banners e patrocinadores.", "Revise em Configurações as permissões e os serviços disponíveis."] },
      { icon: "02", title: "Montar a agenda", description: "Dias, salas, sessões e participações em ordem.", steps: ["Crie os dias, salas e categorias necessárias.", "Na Agenda, adicione cada sessão com horário e duração.", "Dentro da sessão, atribua faculty, funções, títulos e minutos de participação."] },
      { icon: "03", title: "Gerenciar pessoas", description: "Faculty, organizadores, usuários e expositores.", steps: ["Cadastre ou importe o faculty e verifique os dados de contato.", "Atribua funções e permissões somente para o evento ativo.", "Use Usuários e Organograma para controlar responsabilidades e acessos."] },
      { icon: "04", title: "Comunicar e coordenar", description: "WhatsApp, e-mail, convites e acompanhamento.", steps: ["Conecte o WhatsApp e revise o estado da conexão.", "Prepare mensagens ou e-mails segmentados por sessão e participante.", "Acompanhe entregas, respostas e lembretes em Comunicações."] },
      { icon: "05", title: "Abstracts e resultados", description: "Recepção científica, seleção, relatórios e métricas.", steps: ["Configure o Call for Science, modalidades, áreas e datas de submissão.", "Revise trabalhos, atribuições e decisões sem misturar eventos.", "Consulte Relatórios para verificar agenda, participantes e atividade."] },
    ],
  },
};

function localeFor(language: string): "es" | "en" | "pt" {
  const base = language.toLowerCase().split(/[-_]/)[0];
  return base === "en" || base === "pt" ? base : "es";
}

function sameLanguage(first: string, second: string) {
  return first.toLowerCase().replace("_", "-") === second.toLowerCase().replace("_", "-");
}

export function OrganizeUtilityControls({
  languages,
  language,
  onLanguageChange,
  theme,
  onThemeChange,
  loading = false,
}: {
  languages: string[];
  language: string;
  onLanguageChange: (language: string) => void;
  theme: ThemeMode;
  onThemeChange: (theme: ThemeMode) => void;
  loading?: boolean;
}) {
  const [helpOpen, setHelpOpen] = useState(false);
  const copy = helpCopy[localeFor(language)];

  useEffect(() => {
    if (!helpOpen) return;
    const close = (event: KeyboardEvent) => { if (event.key === "Escape") setHelpOpen(false); };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [helpOpen]);

  return <>
    <nav className="organize-utility-pill" aria-label={copy.toolbar} data-no-translate>
      {languages.map((code) => {
        const active = sameLanguage(code, language);
        const title = languageName(code);
        return <button type="button" className={active ? "active" : ""} aria-label={title} title={title} aria-pressed={active} onClick={() => onLanguageChange(code)} key={code}>
          <span aria-hidden="true">{active && loading ? <i className="language-loading" /> : languageFlag(code)}</span>
        </button>;
      })}
      <i className="utility-divider" aria-hidden="true" />
      <button type="button" className="utility-theme" aria-label={theme === "dark" ? copy.light : copy.dark} title={theme === "dark" ? copy.light : copy.dark} onClick={() => onThemeChange(theme === "dark" ? "light" : "dark")}>
        <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{theme === "dark" ? <><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.42 1.42M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.42-1.42M17.66 6.34l1.41-1.41"/></> : <path d="M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8Z"/>}</svg>
      </button>
      <i className="utility-divider" aria-hidden="true" />
      <button type="button" className="utility-help" aria-label={copy.help} title={copy.help} onClick={() => setHelpOpen(true)}>?</button>
    </nav>
    {helpOpen && <div className="organize-help-overlay" role="presentation" data-no-translate onMouseDown={(event) => { if (event.target === event.currentTarget) setHelpOpen(false); }}>
      <section className="organize-help-modal" role="dialog" aria-modal="true" aria-labelledby="organize-help-title">
        <header><div><span>{copy.kicker}</span><h2 id="organize-help-title">{copy.title}</h2><p>{copy.intro}</p></div><button type="button" aria-label={copy.close} title={copy.close} onClick={() => setHelpOpen(false)}>×</button></header>
        <div className="organize-help-topics">{copy.sections.map((section, index) => <details className="organize-help-topic" open={index === 0} key={section.title}>
          <summary><b>{section.icon}</b><span><strong>{section.title}</strong><small>{section.description}</small></span><i aria-hidden="true">⌄</i></summary>
          <ol>{section.steps.map((step) => <li key={step}>{step}</li>)}</ol>
        </details>)}</div>
      </section>
    </div>}
  </>;
}
